import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import ProductsNavMenu from './ProductsNavMenu';
import { APP_NAME, LOGO_SRC, getCurrentYear } from '../../config/branding';
import { buildSigninUrl } from '../../newsintelligence/support/openDiscussions';
import { performClientSignOut } from '../../utils/sessionSignOut';
import { PROFILE_UPDATED_EVENT } from '../../newsintelligence/MainHeader/profilePictureUrl';
import { useProductsSession } from './useProductsSession';
import ProductsAccountMenu from './ProductsAccountMenu';
import { useProductsTheme } from './useProductsTheme';
import './ProductsPage.css';

/**
 * Shared products marketing chrome (header + footer) for /products, /docs, and /d/profile.
 */
export default function ProductsSiteLayout({ children, activeNav = 'products', mainClassName = '' }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [signOutBusy, setSignOutBusy] = React.useState(false);

  const returnTo = `${location.pathname}${location.search}`;
  const signupHref = buildSigninUrl({ mode: 'signup', returnTo });
  const signinHref = buildSigninUrl({ returnTo });
  const { status: sessionStatus, profile, avatarSrc, onAvatarError, refreshSession } = useProductsSession();
  const { isDark, setDarkTheme } = useProductsTheme();
  const isAuthenticated = sessionStatus === 'authenticated' && profile?.userId;
  const isSessionLoading = sessionStatus === 'loading';

  const handleSignOut = React.useCallback(async () => {
    if (signOutBusy) return;
    setSignOutBusy(true);
    try {
      await performClientSignOut();
      await refreshSession();
      navigate('/products');
    } catch {
      /* ignore */
    } finally {
      setSignOutBusy(false);
    }
  }, [navigate, refreshSession, signOutBusy]);

  React.useEffect(() => {
    const onProfileUpdated = () => {
      refreshSession();
    };
    window.addEventListener(PROFILE_UPDATED_EVENT, onProfileUpdated);
    return () => window.removeEventListener(PROFILE_UPDATED_EVENT, onProfileUpdated);
  }, [refreshSession]);

  const mainClasses = ['products-page-main', mainClassName].filter(Boolean).join(' ');

  return (
    <div className="products-page">
      <div className="products-sticky-header">
        <header className="products-topbar">
          <div className="products-band products-topbar-inner">
            <div className="products-topbar-start">
              <ProductsNavMenu activeNav={activeNav} />
              <button
                type="button"
                className="products-logo-btn"
                onClick={() => navigate('/products')}
                aria-label={`${APP_NAME} products`}
              >
                <img src={LOGO_SRC} alt={APP_NAME} className="products-logo" />
              </button>
            </div>
            <div className="products-topbar-end">
              <nav className="products-topbar-nav products-topbar-nav--desktop" aria-label="Account">
                {isSessionLoading ? (
                  <span className="products-nav-placeholder" aria-hidden />
                ) : isAuthenticated ? (
                  <ProductsAccountMenu
                    profile={profile}
                    avatarSrc={avatarSrc}
                    onAvatarError={onAvatarError}
                    onSignOut={handleSignOut}
                    signOutBusy={signOutBusy}
                    isDarkTheme={isDark}
                    onDarkThemeToggle={setDarkTheme}
                  />
                ) : (
                  <>
                    <Link to={signinHref} className="products-nav-link">
                      Sign in
                    </Link>
                    <Link to={signupHref} className="products-nav-cta">
                      Create account
                    </Link>
                  </>
                )}
              </nav>
              <div className="products-topbar-mobile">
                {!isSessionLoading && !isAuthenticated ? (
                  <Link to={signupHref} className="products-nav-cta products-nav-cta--compact">
                    Join
                  </Link>
                ) : null}
                {!isSessionLoading && isAuthenticated ? (
                  <ProductsAccountMenu
                    profile={profile}
                    avatarSrc={avatarSrc}
                    onAvatarError={onAvatarError}
                    onSignOut={handleSignOut}
                    signOutBusy={signOutBusy}
                    compact
                    isDarkTheme={isDark}
                    onDarkThemeToggle={setDarkTheme}
                  />
                ) : null}
                {!isSessionLoading && !isAuthenticated ? (
                  <Link to={signinHref} className="products-nav-link products-nav-link--mobile">
                    Sign in
                  </Link>
                ) : null}
              </div>
            </div>
          </div>
        </header>
      </div>

      <div className={mainClasses}>{children}</div>

      <footer className="products-footer">
        <div className="products-footer-band">
          <div className="products-footer-inner">
            <span className="products-footer-copy">© {getCurrentYear()} {APP_NAME}</span>
            <nav className="products-footer-nav" aria-label="Footer">
            <Link to="/privacy">Privacy</Link>
            <Link to="/docs">Documentation</Link>
            <Link to="/terms">Terms</Link>
            <Link to="/contact">Contact</Link>
            </nav>
          </div>
        </div>
      </footer>
    </div>
  );
}
